// EmailsListPage - View all emails with filtering
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  EnvelopeIcon,
  PaperClipIcon,
  EyeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/20/solid'
import { BuildingOfficeIcon, UserIcon } from '@heroicons/react/24/outline'
import { formatDistanceToNow } from 'date-fns'
import { useTranslation } from '../../hooks/useTranslation'
import { Pagination } from '../../components/ui/Pagination'
import { EmailDetail, EmailDropZone } from '../../components/emails'

type EmailListItem = {
  id: string
  subject: string
  fromEmail: string
  fromName: string | null
  sentAt: string | null
  hasAttachments: boolean
  accountId: string | null
  contactId: string | null
  leadId: string | null
  account: { id: string; name: string } | null
  contact: { id: string; firstName: string | null; lastName: string } | null
  lead: { id: string; firstName: string | null; lastName: string; company: string } | null
}

type EmailListResponse = {
  data: EmailListItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

type FilterType = 'all' | 'linked' | 'unlinked'
type SortField = 'subject' | 'fromEmail' | 'sentAt'
type SortDirection = 'asc' | 'desc'

async function fetchEmails(params: {
  page: number
  limit: number
  filter: FilterType
}): Promise<EmailListResponse> {
  const searchParams = new URLSearchParams()
  searchParams.set('page', params.page.toString())
  searchParams.set('limit', params.limit.toString())
  if (params.filter === 'unlinked') {
    searchParams.set('unlinked', 'true')
  } else if (params.filter === 'linked') {
    searchParams.set('linked', 'true')
  }

  const response = await fetch(`/api/emails?${searchParams.toString()}`, {
    credentials: 'include',
  })

  if (!response.ok) {
    throw new Error('Failed to fetch emails')
  }

  return response.json()
}

export function EmailsListPage() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [filter, setFilter] = useState<FilterType>('all')
  const [sortField, setSortField] = useState<SortField>('sentAt')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)

  const { data: response, isLoading, refetch } = useQuery({
    queryKey: ['emails', 'all', { page, limit, filter }],
    queryFn: () => fetchEmails({ page, limit, filter }),
  })

  const handleFilterChange = (newFilter: FilterType) => {
    setFilter(newFilter)
    setPage(1)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const handleEmailUpdated = () => {
    refetch()
    setSelectedEmailId(null)
  }

  // Client-side sorting
  const sortedEmails = response?.data ? [...response.data].sort((a, b) => {
    let comparison = 0
    switch (sortField) {
      case 'subject':
        comparison = (a.subject || '').localeCompare(b.subject || '')
        break
      case 'fromEmail':
        comparison = (a.fromEmail || '').localeCompare(b.fromEmail || '')
        break
      case 'sentAt':
        comparison = new Date(a.sentAt || 0).getTime() - new Date(b.sentAt || 0).getTime()
        break
    }
    return sortDirection === 'asc' ? comparison : -comparison
  }) : []

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  if (isLoading) return <div className="p-8">{t('common.loading')}</div>

  return (
    <>
      {/* Secondary column - Filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('nav.emails')}</h2>
            <p className="mt-1 text-sm text-gray-500">
              View and manage imported emails
            </p>
          </div>

          {/* Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">{t('common.filter')}</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  filter === 'all'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('common.all')}
              </button>
              <button
                onClick={() => handleFilterChange('linked')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  filter === 'linked'
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Linked
              </button>
              <button
                onClick={() => handleFilterChange('unlinked')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  filter === 'unlinked'
                    ? 'bg-orange-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                Unlinked
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Statistics</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Total</dt>
                <dd className="font-medium text-gray-900">{response?.total || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">{t('pagination.page')}</dt>
                <dd className="font-medium text-gray-900">{response?.page || 1} {t('pagination.of')} {response?.totalPages || 1}</dd>
              </div>
            </dl>
          </div>

          {/* Email Drop Zone */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Import Email</h3>
            <EmailDropZone targetType="auto" onSuccess={() => refetch()} compact />
          </div>

          {/* Help text */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Tip:</strong> Emails can be imported via the Outlook Add-In or by dragging .eml files above.
            </p>
            <a
              href="/outlook-addin-manifest.xml"
              download="justsell-outlook-addin-manifest.xml"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:text-blue-900"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download Outlook Add-In Manifest
            </a>
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs font-medium text-blue-800 mb-2">Install Outlook Add-In:</p>
              <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                <li>Download the manifest file above</li>
                <li>Open Outlook and go to <strong>Get Add-ins</strong></li>
                <li>Click <strong>My add-ins</strong> → <strong>Custom add-ins</strong></li>
                <li>Select <strong>Add from file</strong> and choose the downloaded manifest</li>
                <li>Open any email and click the JustSell icon to send it to your CRM</li>
              </ol>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">{t('nav.emails')}</h1>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => handleFilterChange('all')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {t('common.all')}
              </button>
              <button
                onClick={() => handleFilterChange('linked')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  filter === 'linked' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Linked
              </button>
              <button
                onClick={() => handleFilterChange('unlinked')}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                  filter === 'unlinked' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                Unlinked
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden shadow-sm ring-1 ring-gray-900/5 sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <SortableHeader field="subject">
                          <span className="pl-4 sm:pl-6">{t('fields.subject') || 'Subject'}</span>
                        </SortableHeader>
                        <SortableHeader field="fromEmail">
                          {t('fields.from') || 'From'}
                        </SortableHeader>
                        <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          Linked To
                        </th>
                        <SortableHeader field="sentAt">
                          {t('fields.date') || 'Date'}
                        </SortableHeader>
                        <th className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('common.actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {sortedEmails.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-12 text-center">
                            <EnvelopeIcon className="mx-auto h-12 w-12 text-gray-300" />
                            <p className="mt-2 text-sm text-gray-500">
                              {filter === 'unlinked'
                                ? 'No unlinked emails'
                                : filter === 'linked'
                                ? 'No linked emails'
                                : 'No emails imported yet'}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        sortedEmails.map((email) => (
                          <tr key={email.id} className="hover:bg-gray-50 transition-colors">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 truncate max-w-xs">
                                  {email.subject || '(No subject)'}
                                </span>
                                {email.hasAttachments && (
                                  <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <div className="truncate max-w-xs">
                                {email.fromName || email.fromEmail}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                              {email.account ? (
                                <Link
                                  to={`/accounts/${email.account.id}`}
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                >
                                  <BuildingOfficeIcon className="h-4 w-4" />
                                  <span className="truncate max-w-[150px]">{email.account.name}</span>
                                </Link>
                              ) : email.contact ? (
                                <Link
                                  to={`/contacts/${email.contact.id}`}
                                  className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-800"
                                >
                                  <UserIcon className="h-4 w-4" />
                                  <span>{email.contact.firstName} {email.contact.lastName}</span>
                                </Link>
                              ) : email.lead ? (
                                <Link
                                  to={`/leads/${email.lead.id}`}
                                  className="inline-flex items-center gap-1 text-orange-600 hover:text-orange-800"
                                >
                                  <UserIcon className="h-4 w-4" />
                                  <span>{email.lead.firstName} {email.lead.lastName}</span>
                                </Link>
                              ) : (
                                <span className="text-gray-400 italic">Unlinked</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              {email.sentAt
                                ? formatDistanceToNow(new Date(email.sentAt), { addSuffix: true })
                                : '-'}
                            </td>
                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                              <button
                                onClick={() => setSelectedEmailId(email.id)}
                                className="inline-flex items-center gap-x-1 text-brand-600 hover:text-brand-900"
                              >
                                <EyeIcon className="h-4 w-4" />
                                {t('common.view')}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  {response && response.total > 0 && (
                    <Pagination
                      page={response.page}
                      totalPages={response.totalPages}
                      total={response.total}
                      limit={response.limit}
                      onPageChange={setPage}
                      onLimitChange={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email detail modal */}
      {selectedEmailId && (
        <EmailDetail
          emailId={selectedEmailId}
          onClose={() => setSelectedEmailId(null)}
          onUpdated={handleEmailUpdated}
        />
      )}
    </>
  )
}
